import { useCallback } from 'react';
import { useParams } from 'react-router';
import { EmployeeService } from 'src/api/EmployeeService';
import { invalidateEmployee, invalidateEmployees } from '@stores/employeeStore';

type DeleteEmployeePattern = (
    employeeId: string,
    patternCase: string,
    patternId: string,
) => Promise<true>;

/*
 * Error semantics: dynoapeAPI.delete throws on failure (no internal
 * catch), which skips the invalidation and surfaces to the caller —
 * the planner's own flow handles the error state.
 */
const useDeleteEmployeePattern = (): {
    deleteEmployeePattern: DeleteEmployeePattern;
} => {
    const { departmentId } = useParams<{
        departmentId: string;
    }>();

    const deleteEmployeePattern = useCallback<DeleteEmployeePattern>(
        async (employeeId, patternCase, patternId) => {
            if (!departmentId) {
                throw new Error(
                    'useDeleteEmployeePattern requires a departmentId route param',
                );
            }

            const isDeleted = await EmployeeService.deleteEmployeePattern(
                departmentId,
                employeeId,
                patternId,
                patternCase,
            );

            invalidateEmployee(departmentId, employeeId);
            invalidateEmployees(departmentId);

            return isDeleted;
        },
        [departmentId],
    );

    return { deleteEmployeePattern };
};

export default useDeleteEmployeePattern;
